/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { toast } from 'react-toastify';

import Requests from '../../Services/Requests';
import { STATUS_ENTREGA } from '../../Constants';
import { useAppSelector } from '../../_store/hooks';
import type { Delivery } from '@/_features/deliveries/types';
import { selectUser } from '@/_store/modules/auth/selectors';
import { GET_DELIVERIES_LIST } from '@/_features/deliveries/constants';
import { GET_MONITOR_DELIVERIES } from '@/_features/monitor-deliveries/constants';
import { usePutArrivalAtClient } from '@/features/deliveryTable/api/put-arrival-at-client';
import { ROLE_TYPES, USER_STATE } from '@/Interfaces/Data';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { usePutLeaveEstablishment } from '@/_features/deliveries/api/put-leave-establishment';
import { usePutFinishedByManager } from '@/_features/deliveries/api/put-finished-by-manager';
import { isVersionAtLeast } from '@/_utils/version';

const LEAVE_ESTABLISHMENT_MIN_APP_VERSION = '2.70.0';

export type DeliveryActionTypes =
  | 'REABRIR'
  | 'ASSOCIAR'
  | 'CANCELAR'
  | 'CHEGAR_NA_LOJA'
  | 'ACEITAR_ENTREGA'
  | 'REJEITAR_ENTREGA'
  | 'SAIR_DA_LOJA'
  | 'CHEGOU_NO_CLIENTE'
  | 'ENTREGAR'
  | 'FINALIZAR';

export interface DeliveryAction {
  key: DeliveryActionTypes;
  value: DeliveryActionTypes;
  text: string;
  icon?: IconDefinition;
}

export const DELIVERY_ACTIONS: Record<DeliveryActionTypes, DeliveryAction> = {
  REABRIR: {
    key: 'REABRIR',
    value: 'REABRIR',
    text: 'reabrir',
    icon: undefined,
  },
  ASSOCIAR: {
    key: 'ASSOCIAR',
    value: 'ASSOCIAR',
    text: 'associar',
    icon: undefined,
  },
  CANCELAR: {
    key: 'CANCELAR',
    value: 'CANCELAR',
    text: 'cancelar',
    icon: undefined,
  },
  CHEGAR_NA_LOJA: {
    key: 'CHEGAR_NA_LOJA',
    value: 'CHEGAR_NA_LOJA',
    text: 'chegar na loja',
    icon: undefined,
  },
  ACEITAR_ENTREGA: {
    key: 'ACEITAR_ENTREGA',
    value: 'ACEITAR_ENTREGA',
    text: 'aceitar entrega',
    icon: undefined,
  },
  REJEITAR_ENTREGA: {
    key: 'REJEITAR_ENTREGA',
    value: 'REJEITAR_ENTREGA',
    text: 'rejeitar entrega',
    icon: undefined,
  },
  SAIR_DA_LOJA: {
    key: 'SAIR_DA_LOJA',
    value: 'SAIR_DA_LOJA',
    text: 'sair da loja',
    icon: undefined,
  },
  CHEGOU_NO_CLIENTE: {
    key: 'CHEGOU_NO_CLIENTE',
    value: 'CHEGOU_NO_CLIENTE',
    text: 'chegou no cliente',
    icon: undefined,
  },
  ENTREGAR: {
    key: 'ENTREGAR',
    value: 'ENTREGAR',
    text: 'marcar como entregue',
    icon: undefined,
  },
  FINALIZAR: {
    key: 'FINALIZAR',
    value: 'FINALIZAR',
    text: 'finalizar entrega',
    icon: undefined,
  },
};

export type DeliveryActionTarget = Pick<
  Delivery,
  | '_id'
  | 'status'
  | 'entregadorId'
  | 'nomeEntregador'
  | 'showCancelButton'
  | 'appVersion'
  | 'page'
>;

function useDeliveryAction(
  delivery: DeliveryActionTarget,
  callback?: (action: DeliveryAction) => void,
) {
  const user = useAppSelector(selectUser);

  function invalidateDeliveriesQueries() {
    queryClient.invalidateQueries([GET_DELIVERIES_LIST]);
    queryClient.invalidateQueries([GET_MONITOR_DELIVERIES]);
  }

  const { mutate: handleArriveAtClient } = usePutArrivalAtClient();
  const { mutateAsync: leaveEstablishment } = usePutLeaveEstablishment();
  const { mutate: finishedByManager } = usePutFinishedByManager();

  const [actionLoading, setActionLoading] =
    useState<DeliveryActionTypes | null>(null);

  function actionsStatus(actions: DeliveryAction[]) {
    if (user?.permissions?.delivery.deliveryStatus) {
      return actions;
    }
    return [];
  }

  function actionAssociate() {
    if (user?.permissions?.delivery.associateDelivery) {
      return [DELIVERY_ACTIONS.ASSOCIAR];
    }
    return [];
  }

  function actionCancel() {
    const isLogisticOperator =
      user?.managerLogType === ROLE_TYPES.LOGISTIC_OPERATOR;

    const isUnfinishedDelivery =
      user?.permissions?.delivery.cancelDelivery &&
      delivery.status !== 'FINALIZADO';

    const isFinishedDeliveryAndShouldShowCancelButton =
      user?.permissions?.delivery.cancelDelivery &&
      delivery.status === 'FINALIZADO' &&
      !isLogisticOperator &&
      delivery.showCancelButton;

    if (isUnfinishedDelivery || isFinishedDeliveryAndShouldShowCancelButton) {
      return [DELIVERY_ACTIONS.CANCELAR];
    }
    return [];
  }

  function actionReopen() {
    if (user?.permissions?.delivery.reopenDelivery) {
      return [DELIVERY_ACTIONS.REABRIR];
    }
    return [];
  }

  const availableActions = useMemo(() => {
    switch (delivery.status) {
      case 'PENDENTE':
        return [...actionAssociate(), ...actionCancel()];
      case 'ASSOCIADO': {
        const actionStatusByPage =
          delivery?.page === USER_STATE.ENTREGAS_DISPONIVEIS
            ? []
            : [DELIVERY_ACTIONS.CHEGAR_NA_LOJA];

        return [
          ...actionReopen(),
          ...actionCancel(),
          ...actionsStatus(actionStatusByPage),
        ];
      }
      case 'CHEGOU_NA_LOJA':
        return [
          ...actionReopen(),
          ...actionsStatus([
            DELIVERY_ACTIONS.ACEITAR_ENTREGA,
            DELIVERY_ACTIONS.REJEITAR_ENTREGA,
          ]),
          ...actionCancel(),
        ];
      case 'ACEITO': {
        const actionStatusByPage = (() => {
          switch (delivery?.page) {
            case USER_STATE.DETALHES_DA_CORRIDA:
              return [DELIVERY_ACTIONS.CHEGAR_NA_LOJA];
            case USER_STATE.ENTREGAS_DISPONIVEIS:
              return [
                {
                  ...DELIVERY_ACTIONS.SAIR_DA_LOJA,
                  text: 'personalizar corrida',
                },
              ];
            default:
              return [DELIVERY_ACTIONS.SAIR_DA_LOJA];
          }
        })();

        return [
          ...actionReopen(),
          ...actionsStatus([...actionStatusByPage]),
          ...actionCancel(),
        ];
      }
      case 'PERSONALIZAR_CORRIDA':
        return [
          ...actionReopen(),
          ...actionsStatus([DELIVERY_ACTIONS.SAIR_DA_LOJA]),
          ...actionCancel(),
        ];
      case 'SAIU_DA_LOJA':
        return [...actionsStatus([DELIVERY_ACTIONS.CHEGOU_NO_CLIENTE])];
      case 'CHEGOU_NO_CLIENTE':
        return [...actionsStatus([DELIVERY_ACTIONS.ENTREGAR])];
      case 'ENTREGUE':
        return [...actionsStatus([DELIVERY_ACTIONS.FINALIZAR])];
      case 'FINALIZADO':
        return [...actionCancel()];
      default:
        return [];
    }
  }, [delivery]);

  function handleCallCallback(action: DeliveryAction) {
    if (callback) {
      callback(action);
    }
  }

  async function triggerDeliveryAction(action: DeliveryAction) {
    switch (action.key) {
      case 'REABRIR':
        await reopenDelivery(action);
        break;
      case 'CHEGAR_NA_LOJA':
        await arriveAtPlace(action);
        break;
      case 'ACEITAR_ENTREGA':
        await acceptDelivery(action);
        break;
      case 'REJEITAR_ENTREGA':
        await rejectDelivery(action);
        break;
      case 'SAIR_DA_LOJA':
        await getOutPlace(action);
        break;
      case 'CHEGOU_NO_CLIENTE':
        await arriveAtClient(action);
        break;
      case 'ENTREGAR':
        await deliverDelivery(action);
        break;
      case 'FINALIZAR':
        await finishDelivery(action);
        break;
      default:
        break;
    }
  }

  async function connectDeliveryToMotoboy(
    action: DeliveryAction,
    motoboyId: string,
  ) {
    if (!confirm(`Tem certeza que deseja associar esse entregador?`)) {
      return;
    }

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.associateMotoboy(
        delivery._id,
        motoboyId,
      );

      if (response.r) {
        queryClient.invalidateQueries([GET_MONITOR_DELIVERIES]);
        toast.success('Entregador associado!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function reopenDelivery(action: DeliveryAction) {
    if (!confirm(`Tem certeza que deseja reabrir essa entrega?`)) return;

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.reopenDelivery(
        delivery._id,
      );

      if (response.r) {
        invalidateDeliveriesQueries();
        toast.success('Entrega reaberta!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function cancelDelivery(
    action: DeliveryAction,
    collectTaxOfPlace: boolean,
    passOnTaxForMotoboy: boolean,
  ) {
    if (!confirm(`Tem certeza que deseja cancelar essa entrega?`)) return null;

    const deliveryHasMotoboy =
      delivery.status !== STATUS_ENTREGA.PENDENTE && !!delivery.nomeEntregador;

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.cancelDelivery(
        delivery._id,
        deliveryHasMotoboy ? collectTaxOfPlace : false,
        deliveryHasMotoboy ? passOnTaxForMotoboy : false,
      );

      if (response.r) {
        invalidateDeliveriesQueries();
        toast.success('Entrega cancelada!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function arriveAtPlace(action: DeliveryAction) {
    if (!confirm(`Tem certeza que deseja realizar a ação de chegar na loja?`))
      return null;

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.arriveAtPlace(
        delivery.entregadorId,
      );

      if (response.r) {
        invalidateDeliveriesQueries();
        toast.success('Status alterado para Chegou na loja!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function acceptDelivery(action: DeliveryAction) {
    if (
      !confirm(
        `Tem certeza que deseja fazer o entregador aceitar essa entrega?`,
      )
    )
      return null;

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.acceptDelivery(
        delivery.entregadorId,
        delivery._id,
      );

      if (response.r) {
        invalidateDeliveriesQueries();
        toast.success('Entrega aceita!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function rejectDelivery(action: DeliveryAction) {
    if (
      !confirm(
        `Tem certeza que deseja realizar fazer o entregador essa entrega?`,
      )
    )
      return null;

    setActionLoading(action.key);

    try {
      const response: any = await Requests.deliveries.rejectDelivery(
        delivery.entregadorId,
        delivery._id,
      );

      if (response.r) {
        invalidateDeliveriesQueries();
        toast.success('Entrega rejeitada!');
      } else {
        toast.warn(response.errors.join('-'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function getOutPlace(action: DeliveryAction) {
    if (!confirm(`Tem certeza que deseja realizar a ação de ${action.text}?`))
      return null;

    setActionLoading(action.key);

    try {
      if (
        isVersionAtLeast(
          delivery.appVersion,
          LEAVE_ESTABLISHMENT_MIN_APP_VERSION,
        )
      ) {
        await leaveEstablishment({ deliverymanId: delivery.entregadorId });
      } else {
        await Requests.deliveries.getOutPlace(delivery.entregadorId);
      }

      invalidateDeliveriesQueries();
      toast.success('Status alterado para Saiu da loja!');
    } catch (err: any) {
      toast.error('Não foi possível realizar a ação');
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function arriveAtClient(action: DeliveryAction) {
    if (
      !confirm(`Tem certeza que deseja realizar a ação de chegar no cliente?`)
    )
      return null;

    setActionLoading(action.key);

    handleArriveAtClient(
      {
        deliverymanId: delivery.entregadorId,
        deliveryId: delivery._id,
      },
      {
        onSuccess: () => {
          invalidateDeliveriesQueries();
        },
        onSettled: () => {
          setActionLoading(null);
          handleCallCallback(action);
        },
      },
    );
  }

  async function deliverDelivery(action: DeliveryAction) {
    if (
      !confirm(`Tem certeza que deseja realizar a ação de entregar ao cliente?`)
    )
      return null;

    setActionLoading(action.key);

    try {
      await Requests.deliveries.deliverDelivery(
        delivery.entregadorId,
        delivery._id,
      );

      invalidateDeliveriesQueries();
      toast.success('Status alterado para Entregue!');
    } catch (err: any) {
      toast.error('Não foi possível realizar a ação');
    } finally {
      setActionLoading(null);
      handleCallCallback(action);
    }
  }

  async function finishDelivery(action: DeliveryAction) {
    if (
      !confirm(`Tem certeza que deseja realizar a ação de finalizar entrega?`)
    )
      return null;

    setActionLoading(action.key);

    finishedByManager(
      {
        deliverymanId: delivery.entregadorId,
        deliveryId: delivery._id,
      },
      {
        onSuccess: () => {
          invalidateDeliveriesQueries();
          toast.success('Status alterado para Finalizado!');
        },
        onSettled: () => {
          setActionLoading(null);
          handleCallCallback(action);
        },
      },
    );
  }

  return {
    availableActions,
    triggerDeliveryAction,
    actionLoading,
    connectDeliveryToMotoboy,
    cancelDelivery,
  };
}

export default useDeliveryAction;
